/**
 * @fileoverview CategoryBadge — the single way a time category is drawn
 * (MOR-906), mirroring `RoleBadge`: firm-status colour + name in a
 * soft Badge, with a hover helper (name, IPS analysis code) so the colour
 * coding reads the same everywhere — entry rows, pickers, calendar.
 * Also exports `CategoryIconPicker` for the settings editor (same visual
 * language as `IconSwatchPicker`, over the timesheet icon set).
 */

import { X } from "lucide-react";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import { Badge } from "@/ui/primitives";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";
import { CATEGORY_ICON_KEYS, CATEGORY_ICONS } from "./categoryVisuals";

export interface CategoryBadgeCategory {
  name: string;
  color?: TaskColor;
  icon?: string;
  ipsCode?: string;
}

export function CategoryBadge({
  category,
  size = "sm",
  className,
}: {
  category: CategoryBadgeCategory;
  size?: "sm" | "md";
  className?: string;
}) {
  const badge = (
    <Badge
      color={category.color ?? "gray"}
      variant="soft"
      size={size}
      className={cn("gap-1", className)}
    >
      {category.name}
    </Badge>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-medium">{category.name}</p>
          <p className="text-muted-foreground">
            {category.ipsCode
              ? `IPS/Insol code ${category.ipsCode}`
              : "No IPS/Insol code mapped"}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Small coloured dot for tight spots (select items). */
export function CategoryGlyph({
  color,
  className,
}: {
  color?: TaskColor;
  /** Accepted so existing call sites keep compiling; icons are no longer drawn. */
  icon?: string;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <span
        className={cn(
          "size-2 shrink-0 rounded-full",
          TASK_COLOR_DOT[color ?? "gray"],
        )}
      />
    </span>
  );
}

/**
 * Icon picker over the timesheet icon set — same visual language as the
 * workflow-role `IconSwatchPicker` (dashed X cell clears the selection).
 */
export function CategoryIconPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (icon: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        onClick={() => onChange(null)}
        className={cn(
          "flex size-7 items-center justify-center rounded-md border border-dashed text-muted-foreground transition-colors hover:bg-muted",
          value === null && "ring-2 ring-ring ring-offset-1",
        )}
        aria-label="No icon"
      >
        <X className="size-3.5" />
      </button>
      {CATEGORY_ICON_KEYS.map((key) => {
        const { Icon, label } = CATEGORY_ICONS[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            title={label}
            className={cn(
              "flex size-7 items-center justify-center rounded-md border transition-colors hover:bg-muted",
              value === key && "ring-2 ring-ring ring-offset-1",
            )}
            aria-label={label}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}
